/**
 * useDesktopOAuth — Tauri-only OAuth hook.
 *
 * Since Aug 26, 2026 the PRIMARY login on desktop is the inline
 * multi-step BFF form (``DesktopLoginForm``) — the user never sees
 * Zitadel's hosted UI. This hook is now used for TWO narrower jobs:
 *
 *   1. **Vault hydrate.** On boot it loads any tokens persisted from
 *      a prior session (OS keychain via ``tokenStore``) and exposes
 *      them to ``/login``'s ``DesktopLogin`` so a returning user
 *      skips the form and lands on the workspace.
 *   2. **SSO fallback receiver.** ``signIn()`` still implements the
 *      RFC 8252 native-app dance for the "Continua con SSO nel
 *      browser" button (and any org-scoped IdP path). Since Aug 26,
 *      2026 the callback is delivered via a LOOPBACK redirect
 *      (``http://127.0.0.1:<port>/callback``) rather than the older
 *      ``ando://callback`` deep-link — see the loopback migration
 *      note below. On success the tokens land in the OS keychain via the
 *      SAME persistence helpers (``desktopSession``) the BFF form
 *      uses, so both paths converge on one cache shape.
 *
 * Loopback migration (Aug 26, 2026)
 * ─────────────────────────────────
 * The old deep-link path had two production sores:
 *   - macOS opens custom-scheme URLs by "activating" ONE registered
 *     app, but two Tauri builds side-by-side (dev + prod) race for
 *     ownership of ``ando://`` — the wrong binary was launched more
 *     than once in early-access QA.
 *   - Windows requires HKCR write for scheme registration; a plain
 *     user install couldn't self-register, and the fallback pop-up
 *     froze the flow.
 *
 * The RFC 8252 loopback dance sidesteps both by having the Rust
 * shell bind an ephemeral 127.0.0.1 port for the duration of ONE
 * attempt, feed the port back into ``redirect_uri``, and shut down
 * the listener as soon as the code arrives. No OS state changes; no
 * cross-binary races.
 *
 * The silent-refresh scheduler + focus/visibility prewarm below are
 * transport-agnostic — they operate on whatever tokens are in the
 * vault, regardless of which login path deposited them.
 */

import { invoke } from "@tauri-apps/api/core";
import { listen, type UnlistenFn } from "@tauri-apps/api/event";
import { useLingui } from "@lingui/react/macro";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  exchangeDesktopCode,
  refreshDesktopTokens,
  type DesktopTokensResponse,
} from "@ando/core";

import {
  persistDesktopSession,
  persistDesktopSessionCookie,
  tokensToDesktopState,
} from "./desktopSession";
import { buildPkcePair } from "./pkce";
import {
  coerceErrorMessage,
  startLoopbackServer,
  stopLoopbackServer,
} from "./loopbackServer";
import {
  clearDesktopTokens,
  isAccessTokenExpired,
  loadDesktopTokens,
  saveDesktopTokens,
  type DesktopTokenState,
} from "./tokenStore";

interface AuthorizeUrlOpts {
  /**
   * When true, appends ``prompt=select_account`` to the ``/authorize``
   * URL so Zitadel forces the account-picker even when the browser has
   * a live IdP session. Used by the "Cambia account" affordance under
   * the BFF form.
   */
  readonly forceAccountPicker?: boolean;
  /**
   * Optional Zitadel IdP id. When set, appended as ``idp_id=<...>`` so
   * the hosted flow jumps straight to that provider (used by org-scoped
   * IdP buttons — Google Workspace, Entra ID, etc.). Nullable so
   * callers can pass ``undefined``/``null`` interchangeably without
   * having to omit the whole opts object.
   */
  readonly idpId?: string | null;
}

/**
 * Build the Zitadel ``/authorize`` URL. Reads first from the runtime
 * config bag (``window.__ANDO_RUNTIME__`` injected via
 * ``/runtime-config.js`` at container boot — Docker single-image
 * pattern), then falls back to ``import.meta.env.VITE_ZITADEL_*`` so
 * local dev builds without runtime config still work.
 */
function buildAuthorizeUrl(
  challenge: string,
  state: string,
  redirectUri: string,
  opts: AuthorizeUrlOpts = {},
): string {
  const runtime = typeof window !== "undefined" ? window.__ANDO_RUNTIME__ : undefined;
  const authority =
    runtime?.zitadelAuthority ??
    (import.meta.env.VITE_ZITADEL_AUTHORITY as string | undefined) ??
    "http://localhost:8080";
  const clientId =
    runtime?.zitadelDesktopClientId ??
    (import.meta.env.VITE_ZITADEL_DESKTOP_CLIENT_ID as string | undefined) ??
    (import.meta.env.VITE_ZITADEL_CLIENT_ID as string | undefined) ??
    "";
  const params = new URLSearchParams({
    response_type: "code",
    client_id: clientId,
    redirect_uri: redirectUri,
    scope: "openid profile email offline_access",
    state,
    code_challenge: challenge,
    code_challenge_method: "S256",
  });
  if (opts.forceAccountPicker) {
    // Zitadel honours the standard OIDC ``prompt=select_account`` — an
    // IdP that ignores it will just show its normal login UI, which is
    // still what the user asked for.
    params.set("prompt", "select_account");
  }
  if (opts.idpId) {
    // Zitadel-specific short-circuit into a named IdP. When absent the
    // hosted UI presents the org's configured picker.
    params.set("idp_id", opts.idpId);
  }
  return `${authority.replace(/\/$/, "")}/oauth/v2/authorize?${params.toString()}`;
}

interface InflightAttempt {
  readonly state: string;
  readonly verifier: string;
  readonly redirectUri: string;
  readonly port: number;
  readonly startedAt: number;
}

export interface UseDesktopOAuthResult {
  /** Current session state. ``null`` until the first vault hydrate completes. */
  readonly tokens: DesktopTokenState | null;
  /** True while a vault hydrate or token exchange is in flight. */
  readonly isLoading: boolean;
  /** Last error from a failed exchange / refresh (cleared on retry). */
  readonly error: string | null;
  /**
   * Kick off the IdP browser flow. Resolves when the browser opens.
   * Optional opts route through to the ``/authorize`` URL — pass
   * ``forcePicker`` to force the Zitadel account-picker, ``idpId`` to
   * jump straight into a named IdP.
   */
  readonly signIn: (opts?: {
    readonly forcePicker?: boolean;
    readonly idpId?: string | null;
  }) => Promise<void>;
  /** Drop the tokens, clear the vault. */
  readonly signOut: () => Promise<void>;
  /** Manually refresh — usually triggered by the silent scheduler. */
  readonly refresh: () => Promise<DesktopTokensResponse | null>;
}

// ``tokensToState`` moved to ``./desktopSession`` (Aug 26, 2026) so
// the inline BFF form and the SSO loopback path converge on a single
// vault-write + cookie-seed sequence. Re-exposed here as a thin alias
// so the diff below stays legible and future readers see the single
// source of truth.
const tokensToState = tokensToDesktopState;

export function useDesktopOAuth(): UseDesktopOAuthResult {
  const { t } = useLingui();
  const [tokens, setTokens] = useState<DesktopTokenState | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const inflightRef = useRef<InflightAttempt | null>(null);

  // Precompute every user-facing string the async paths need. Locked
  // behind a ref so ``handleCallback`` / ``doRefresh`` never re-bind on
  // locale swap (see task 3 in the Aug 26, 2026 hardening pass — a
  // callback in flight would otherwise be dropped mid-exchange when the
  // effect that owns the ``oauth-callback-loopback`` listener re-runs).
  //
  // ``t`` from ``useLingui`` is a fresh identity every render; the ref
  // captures the LATEST value so async paths still see fresh translations
  // when the user swaps locale AFTER a listener was bound.
  const errors = useMemo(
    () => ({
      missing: t`Callback OAuth privo di code/state. Riprova.`,
      csrf: t`State mismatch nel callback OAuth. Possibile CSRF — riprova.`,
      exchange: t`Login fallito durante l'exchange.`,
      browser: t`Impossibile aprire il browser.`,
      refresh: t`Refresh fallito.`,
    }),
    [t],
  );
  const errorsRef = useRef(errors);
  useEffect(() => {
    errorsRef.current = errors;
  }, [errors]);

  /**
   * Process ONE loopback ``/callback`` bounce. Parses the URL, verifies
   * the ``state`` matches the in-flight attempt (CSRF guard, RFC 6749
   * §10.12), then exchanges the code with the BFF and lands the tokens
   * in the OS keychain via ``persistDesktopSession``.
   *
   * SILENT-SKIP CONTRACT — TWO hooks subscribe to
   * ``oauth-callback-loopback`` (``useDesktopOAuth`` +
   * ``useDesktopIdpLogin``). We discriminate by payload SHAPE, not by a
   * synthetic query param: the OAuth standard callback carries
   * ``code`` + ``state``, while the Zitadel v2 IdP-intent callback
   * carries ``id`` + ``token``. A payload without our two params
   * belongs to the sibling and is silently skipped so a fast
   * double-click on two different login buttons doesn't flash a
   * misleading banner on the passive hook. This shape-based approach
   * keeps the ``redirect_uri`` exact-match with the Zitadel client
   * whitelist (adding ``?flow=…`` would break RFC 6749 §3.1.2 exact
   * match against a whitelisted URI). The ``inflightRef`` guard stays
   * as a second layer for callbacks that arrive after a manual cancel.
   */
  const handleCallback = useCallback(async (callbackUrl: string) => {
    // The Rust listener hands us a fully-qualified URL like
    // ``http://127.0.0.1:<port>/callback?code=...&state=...``.
    // Parse via ``URL`` so we validate the shape at the same time.
    let parsed: URL;
    try {
      parsed = new URL(callbackUrl);
    } catch {
      // Malformed payload — can't tell whose it was. Silent skip.
      return;
    }

    const code = parsed.searchParams.get("code");
    const receivedState = parsed.searchParams.get("state");
    if (!code || !receivedState) {
      // Sibling hook's IdP-intent callback (``id`` + ``token`` shape)
      // or a stale/malformed URL. Silent skip — see contract above.
      return;
    }

    const attempt = inflightRef.current;
    if (!attempt) {
      // Late arrival after a manual cancel / unmount. Silent skip.
      return;
    }

    const currentErrors = errorsRef.current;

    if (attempt.state !== receivedState) {
      // CSRF (RFC 6749 §10.12) — refuse and tear the listener down so
      // the attacker can't reuse the loopback port for a second try.
      await stopLoopbackServer(attempt.port);
      inflightRef.current = null;
      setError(currentErrors.csrf);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      const resp = await exchangeDesktopCode({
        code,
        code_verifier: attempt.verifier,
        redirect_uri: attempt.redirectUri,
      });
      const next = await persistDesktopSession(resp);
      setTokens(next);
      setError(null);
    } catch (err) {
      setError(coerceErrorMessage(err, currentErrors.exchange));
    } finally {
      await stopLoopbackServer(attempt.port);
      inflightRef.current = null;
      setIsLoading(false);
    }
  }, []);

  // ── Boot: vault hydrate + loopback listener ─────────────────────────
  useEffect(() => {
    let unlisten: UnlistenFn | undefined;
    let cancelled = false;

    (async () => {
      // 1. Restore any tokens persisted from a previous session.
      const persisted = await loadDesktopTokens();
      if (!cancelled) {
        setTokens(persisted);
        setIsLoading(false);
      }

      // 2. Subscribe to the loopback callback event. The Rust shell
      //    binds an ephemeral 127.0.0.1 port per ``signIn`` attempt
      //    (``start_local_oauth_server``) and emits this event with
      //    the full callback URL when the IdP redirects back. See the
      //    loopback migration note in the file docstring.
      //
      //    No cold-start drain step: unlike the deep-link path, the
      //    OS can't dispatch a loopback bounce before the process is
      //    up — the listener wouldn't exist. The old
      //    ``read_pending_oauth_callback`` drain was deep-link-only.
      try {
        unlisten = await listen<string>("oauth-callback-loopback", (event) => {
          void handleCallback(event.payload);
        });
      } catch {
        // Not in Tauri context — silently skip. The login screen
        // checks ``getEnv().isTauri`` before mounting this hook so we
        // shouldn't get here, but guard anyway.
      }
    })();

    return () => {
      cancelled = true;
      if (unlisten) unlisten();
    };
  }, [handleCallback]);

  // ``doRefresh`` needs the LATEST tokens but must NOT re-create every
  // render — the schedulers below arm timers / listeners that depend on
  // ``doRefresh`` identity. Ref indirection keeps ``doRefresh`` stable
  // (empty deps) while still reading fresh state each call. Same trick
  // as ``errorsRef`` above so a locale swap mid-flight doesn't churn
  // any of the callbacks the effects have already bound.
  const tokensRef = useRef(tokens);
  useEffect(() => {
    tokensRef.current = tokens;
  }, [tokens]);

  const doRefresh = useCallback(async (): Promise<DesktopTokensResponse | null> => {
    const current = tokensRef.current ?? (await loadDesktopTokens());
    if (!current?.refresh_token) return null;
    try {
      const resp = await refreshDesktopTokens({
        refresh_token: current.refresh_token,
      });
      const next = tokensToState(resp);
      await saveDesktopTokens(next);
      persistDesktopSessionCookie(resp);
      setTokens(next);
      setError(null);
      return resp;
    } catch (err) {
      setError(coerceErrorMessage(err, errorsRef.current.refresh));
      // The user gets bounced to /login on the next 401. We don't
      // proactively sign them out here because a transient 5xx on
      // the IdP shouldn't nuke the session.
      return null;
    }
  }, []);

  // ── Silent refresh scheduler ─────────────────────────────────────────
  //
  // Refresh at ~75% of the lifetime, with a 60s floor so a clock skew on
  // the host doesn't immediately re-trigger. ``doRefresh`` is stable
  // (ref-indirected above) so the effect's dep array can carry it
  // without triggering re-arms every render — no ``exhaustive-deps``
  // disable needed.
  useEffect(() => {
    if (!tokens) return;
    const refreshIn = Math.max(
      Math.floor((tokens.expires_at - Date.now()) * 0.75),
      60_000,
    );
    const timer = window.setTimeout(() => {
      void doRefresh();
    }, refreshIn);
    return () => window.clearTimeout(timer);
  }, [tokens, doRefresh]);

  // ── Focus / visibility prewarm (June 18, 2026) ──────────────────────
  //
  // The 75% scheduler above is set via ``window.setTimeout``, which the OS
  // SUSPENDS while the laptop sleeps. A 12h token armed at noon would fire
  // at ~9PM in JS-time, but if the lid is closed for 6h the timer fires
  // 6h late — the access_token has been expired for hours by then, and
  // the first request after wake-up takes the 401-retry latency hit.
  //
  // Belt-and-braces: on tab focus / Tauri window focus / visibility back
  // to ``visible``, if the cached token has <5 min of life left, kick a
  // silent refresh proactively. Idempotent — ``refreshAccessToken`` (via
  // the registered strategy in ``main.tsx``) coalesces concurrent calls
  // through the mutex in ``tokenRefresh.ts``, so a focus-driven refresh
  // racing with the scheduler-driven one collapses into ONE actual
  // ``/auth/desktop/refresh`` round-trip. Reads tokens via ``tokensRef``
  // so the listener stays bound across token rotations — otherwise every
  // silent refresh would re-arm the focus listener and lose queued
  // ``focus`` events during the swap.
  useEffect(() => {
    const PROACTIVE_MS = 5 * 60_000;
    const maybeRefresh = (): void => {
      const current = tokensRef.current;
      if (!current) return;
      const remaining = current.expires_at - Date.now();
      if (remaining <= PROACTIVE_MS) void doRefresh();
    };
    const onVisibility = (): void => {
      if (document.visibilityState === "visible") maybeRefresh();
    };
    window.addEventListener("focus", maybeRefresh);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("focus", maybeRefresh);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [doRefresh]);

  // ── Public actions ───────────────────────────────────────────────────

  const signIn = useCallback(
    async (opts?: { readonly forcePicker?: boolean; readonly idpId?: string | null }) => {
      setError(null);
      setIsLoading(true);
      let acquiredPort: number | null = null;
      try {
        // 1. Bind an ephemeral loopback listener. Rust returns the OS-
        //    assigned port so we can build the ``redirect_uri`` the IdP
        //    will bounce back to.
        const port = await startLoopbackServer();
        acquiredPort = port;
        // Bare loopback ``redirect_uri`` (no synthetic query params) so
        // it matches the Zitadel client whitelist exactly per RFC 6749
        // §3.1.2. Discrimination from the sibling ``useDesktopIdpLogin``
        // hook's callback happens in ``handleCallback`` by payload shape
        // (``code`` + ``state`` vs ``id`` + ``token``).
        const redirectUri = `http://127.0.0.1:${port}/callback`;

        // 2. PKCE (verifier stays in-memory for the exchange step) +
        //    a fresh CSRF state.
        const { verifier, challenge, state } = await buildPkcePair();

        // 3. Stash the attempt BEFORE opening the browser so a very
        //    fast callback (edge case: cached IdP session, one-click
        //    consent) can't beat the ref write.
        inflightRef.current = {
          verifier,
          state,
          redirectUri,
          port,
          startedAt: Date.now(),
        };

        // 4. Build the ``/authorize`` URL and open the system browser.
        const authorizeUrl = buildAuthorizeUrl(challenge, state, redirectUri, {
          forceAccountPicker: opts?.forcePicker,
          idpId: opts?.idpId ?? null,
        });
        await invoke<void>("start_oauth_login", { authorizeUrl });
      } catch (err) {
        // Tauri ``invoke`` rejects with the raw string returned by the
        // Rust command's ``Err(String)`` — NOT a JS ``Error`` instance.
        // Coerce to a readable message so the user sees the actual
        // reason (e.g. "authorize_url must be https — got 'http'")
        // instead of the generic fallback.
        setError(coerceErrorMessage(err, errorsRef.current.browser));
        // If we bound the loopback port before the browser open failed
        // we must tear it down — otherwise the port stays held for the
        // life of the process.
        if (acquiredPort !== null) await stopLoopbackServer(acquiredPort);
        inflightRef.current = null;
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  const signOut = useCallback(async () => {
    await clearDesktopTokens();
    setTokens(null);
  }, []);

  return {
    tokens,
    isLoading,
    error,
    signIn,
    signOut,
    refresh: doRefresh,
  };
}

/**
 * Synchronous-ish helper for the ApiClient ``authTokenProvider``.
 *
 * Returns the current access token if non-expired, ``null`` otherwise.
 * The silent-refresh scheduler in ``useDesktopOAuth`` handles
 * proactive rotation; this guard catches the edge case where a request
 * fires between the expiry and the next scheduler tick.
 */
export async function getDesktopAccessToken(): Promise<string | null> {
  const state = await loadDesktopTokens();
  if (!state) return null;
  if (isAccessTokenExpired(state)) return null;
  return state.access_token;
}
